import { getIsStandalonePWA } from "components/PWA/helpers/getIsStandalonePWA";
import { analyticsTrackerV2 } from "contexts/ScopelyIdentityProvider/configv2";
import sequenceInstance from "contexts/SessionSequenceProvider";
import { LOCAL_STORAGE_TIME_ZONE } from "debug/components/SetLocalTime";
import { browserName, isMobile } from "react-device-detect";
import { getPlatform } from "utils/platform";
import { getLocalDateEtcGMTFormat } from "utils/time";
import {
	IBuyProps,
	IInvoice,
	IInvoiceReponse,
	IWebStoreContext,
	IWebStoreContextApiProps
} from "../types";

interface IGetBuyProps extends IWebStoreContextApiProps {
	isMdDown: boolean;
	xsollaTokenOverrideId: string;
	logout: () => void;
}

const getBuy = ({
	axios,
	authToken,
	backendUri,
	popups,
	xsollaTokenOverrideId,
	dryRunOverride,
	createHeaders,
	debug,
	syntheticId,
	sessionId
}: IGetBuyProps): IWebStoreContext["buy"] => {
	const buy = async (props: IBuyProps): Promise<IInvoice | undefined> => {
		const localTimeZoneOverride = localStorage.getItem(LOCAL_STORAGE_TIME_ZONE);
		debug.log({ iconText: "buy:", message: [`Buying ${props?.offerId}`] });

		/* CURRENT SETTINGS (hardcoded on server, overrides here for development purposes)
		const data = {
			xsollaTokenOverride: {
				settings: {
					ui: {
						// version: isMdDown ? "mobile" : "desktop",
						mobile: {
							header: {
								close_button: false
							},
							footer: {
								is_visible: false
							}
						},
						desktop: {
							header: {
								is_visible: true
							}
						}
					},
					redirect_policy: {
						manual_redirection_action: "postmessage"
					}
				}
			}
		};
		*/

		const data = {
			xsollaTokenOverride: {}
		};

		const queryParams: {
			offerId: string;
			dryRun?: string;
			xsollaTokenOverrideId?: string;
			dipId?: string;
			fromHud?: boolean;
			waterfallSaleId?: string;
			waterfallSaleTierIndex?: number;
			endlessOfferEventId?: string;
			endlessOfferNodeIndex?: number;
			drip_offer_event_id?: string;
			upsellConfigId?: string;
			upsellSourceOfferId?: string;
			upsellOfferId?: string;
			friendGiftShopOfferId?: string;
			friendUserId?: string;
			friendName?: string;
		} = {
			offerId: props?.offerId
		};

		if (dryRunOverride) {
			queryParams.dryRun = dryRunOverride;
		}

		if (xsollaTokenOverrideId) {
			queryParams.xsollaTokenOverrideId = xsollaTokenOverrideId;
		}

		if (props?.dipId) {
			queryParams.dipId = props.dipId;
		}

		if (props?.fromHud) {
			queryParams.fromHud = true;
		}

		if (props?.waterfallSaleId) {
			queryParams.waterfallSaleId = props?.waterfallSaleId;
		}

		if (typeof props?.waterfallSaleTierIndex === "number") {
			queryParams.waterfallSaleTierIndex = props?.waterfallSaleTierIndex;
		}

		if (props?.endlessOfferEventId) {
			queryParams.endlessOfferEventId = props?.endlessOfferEventId;
		}

		if (typeof props?.endlessOfferNodeIndex === "number") {
			queryParams.endlessOfferNodeIndex = props?.endlessOfferNodeIndex;
		}

		if (props?.drip_offer_event_id) {
			queryParams.drip_offer_event_id = props?.drip_offer_event_id;
		}

		if (props?.upsellSourceOfferId) {
			queryParams.upsellSourceOfferId = props?.upsellSourceOfferId;
		}

		if (props?.upsellConfigId) {
			queryParams.upsellConfigId = props?.upsellConfigId;
		}

		if (props?.upsellOfferId) {
			queryParams.upsellOfferId = props?.upsellOfferId;
		}

		if (props?.friendGiftShopOfferId !== undefined) {
			queryParams.friendGiftShopOfferId = props.friendGiftShopOfferId;
		}

		if (props?.friendUserId) {
			queryParams.friendUserId = props.friendUserId;
		}

		if (props?.friendName) {
			queryParams.friendName = props.friendName;
		}

		const deviceToken = analyticsTrackerV2.getDeviceToken();
		const platform = getPlatform();
		const headersConfig: Record<string, string> = {
			"Content-Type": "text/plain; charset=utf-8",
			Authorization: `Bearer ${authToken?.value}`,
			"X-GSN-WEBSTORE-DEVICE-TYPE": isMobile ? "mobile" : "desktop",
			"X-GSN-WEBSTORE-BROWSER-TYPE": browserName,
			"X-GSN-WEBSTORE-SYNTHETIC-ID": syntheticId,
			"X-SCOPELY-WEBSTORE-SESSION-ID": sessionId,
			"X-SCOPELY-WEBSTORE-LAUNCH-SOURCE": getIsStandalonePWA() ? "pwa" : "web",
			"X-SCOPELY-WEBSTORE-SESSION-REQUEST-SEQUENCE": sequenceInstance
				.incrementAndGetSequence()
				.toString(),
			"X-GSN-WEBSTORE-REFERER-TIMEZONE": localTimeZoneOverride
				? localTimeZoneOverride
				: getLocalDateEtcGMTFormat(),
			"X-SCOPELY-WEBSTORE-PLATFORM": platform,
			"X-TITAN-DEVICE-TOKEN": deviceToken ? deviceToken : "",
			"X-TITAN-METADATA": JSON.stringify({
				app_version: process.env.REACT_APP_WEBSTORE_RELEASE_VERSION,
				environment_id: process.env.REACT_APP_SCOPELY_ANALYTICS_ENVIRONMENT_ID
			})
		};

		const requestConfig = {
			baseURL: backendUri,
			headers: createHeaders(headersConfig),
			params: queryParams
		};

		const uri = "/rest/store/invoice";
		const invoiceResponse = await axios
			.post<IInvoiceReponse>(uri, data, requestConfig)
			.then(async (response) => {
				//NOTE:bfloyd test errors
				// response.data.success = false;
				// response.data.erroorCode = "test_code";
				// response.data.errorMessage = "test_message";

				if (response.data && response.data.success) {
					debug.log({
						iconText: "buy:",
						message: ["invoiceResponse:", response.data]
					});
					return response.data;
				} else {
					console.error(response);
					popups.handleErrorResponse({ response });
				}
			})
			.catch((err) => {
				console.error(err.message);
				popups.handleErrorResponse(err);
			});

		return invoiceResponse?.invoice;
	};
	return buy;
};

export default getBuy;
